import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import CommonTable from "../../../common/table/ComonTable";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import { primaryColor } from "../../../../utils/comon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faDownload } from "@fortawesome/free-solid-svg-icons";
import { generateInvoicePDF } from "../../../../utils/generateInvoicePDF";

const MaintenanceSetup = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 10, totalRecords: 0 });
    const siteId = storageService.getItem("siteId") || 1;

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const res = await apiServices({
                method: "GET",
                url: `/maintenance/site/${2}`,
                useNodeApi: true
            });
            if (res?.success && res?.data?.data) {
                const fetchedList = Array.isArray(res.data.data) ? res.data.data : [];
                setInvoices(fetchedList);
                setPagination(prev => ({ ...prev, totalRecords: fetchedList.length }));
            }
        } catch (error) {
            console.error("Failed to fetch site invoices");
        }
        setLoading(false);
    };

    const handleMarkPaid = async (invoiceId) => {

        setLoading(true);
        try {
            const res = await apiServices({
                method: "PUT",
                url: `/maintenance/invoice/${invoiceId}/pay`,
                useNodeApi: true
            });
            if (res?.success) {
                toast.success("Invoice marked as paid successfully!");
                fetchInvoices();
            } else {
                toast.error(res?.message || "Failed to mark invoice as paid");
            }
        } catch (error) {
            toast.error("Error updating invoice status");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, [siteId]);

    const tableHeaders = [
        { label: "REQUEST ID", key: "requestId", align: "left" },
        { label: "CATEGORY", key: "category" },
        { label: "TYPE", key: "requestType" },
        { label: "BASE AMOUNT", key: "baseAmount" },
        { label: "START DATE", key: "startDate" },
        { label: "DUE DATE", key: "dueDate" }
    ];

    const getStatusStyles = (status) => {
        const normalizedStatus = (status || "").toUpperCase();
        switch (normalizedStatus) {
            case "PAID": return "bg-[#DCFCE7] text-[#166534]";
            case "EXPIRED": return "bg-[#FEE2E2] text-[#991B1B]";
            case "CARRIED_FORWARD": return "bg-[#F3F4F6] text-[#374151]";
            case "PENDING": return "bg-[#FEF9C3] text-[#D97706]";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    const renderTableCell = (key, row) => {
        switch (key) {
            case "requestId": return <span className="font-bold text-xs text-[#191C1E] dark:text-textprimary">REQ-{row?.RequestId || "—"}</span>;
            case "category": return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{row?.Category || "—"}</span>;
            case "requestType": return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{row?.RequestType?.replace('_', ' ') || "—"}</span>;
            case "baseAmount": return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">₹{row?.BaseAmount || "0"}</span>;
            case "startDate":
                const s = row?.StartDate ? new Date(row.StartDate).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—";
                return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{s}</span>;
            case "dueDate":
                const d = row?.DueDate ? new Date(row.DueDate).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—";
                return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{d}</span>;
            default: return "—";
        }
    };

    const expandedRowRender = (row) => {
        if (!row.invoices || row.invoices.length === 0) {
            return <div className="p-4 text-xs text-gray-500 dark:text-textsecondary">No invoices found for this request.</div>;
        }

        return (
            <div className="p-4 bg-gray-50/50 dark:bg-actionitembg rounded-lg m-2 border border-gray-100 dark:border-bordercolor">
                <h4 className="text-xs font-bold text-gray-700 dark:text-textprimary mb-3 uppercase tracking-wider">Unit Invoices</h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                        <thead className="text-gray-500 dark:text-textsecondary border-b border-gray-200 dark:border-bordercolor">
                            <tr>
                                <th className="pb-2 font-semibold">INVOICE NO</th>
                                <th className="pb-2 font-semibold">UNIT / ADMIN</th>
                                <th className="pb-2 font-semibold">MOBILE</th>
                                <th className="pb-2 font-semibold">AMOUNT</th>
                                <th className="pb-2 font-semibold">STATUS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {row.invoices.map((inv, idx) => (
                                <tr key={idx} className="border-b border-gray-100 dark:border-bordercolor last:border-0">
                                    <td className="py-3 font-medium text-gray-800 dark:text-textprimary">{inv.InvoiceNumber || "—"}</td>
                                    <td className="py-3">
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-800 dark:text-textprimary">{inv.UnitName || "—"} ({inv.UnitNumber || "—"})</span>
                                            <span className="text-[10px] text-gray-500 dark:text-textsecondary">{inv.UnitAdminFirstName || ""} {inv.UnitAdminLastName || ""}</span>
                                        </div>
                                    </td>
                                    <td className="py-3 text-gray-600 dark:text-textsecondary">{inv.UnitAdminMobile || "—"}</td>
                                    <td className="py-3 font-medium text-gray-800 dark:text-textprimary">₹{inv.TotalAmount || "0"}</td>
                                    <td className="py-3 flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider uppercase ${getStatusStyles(inv.Status)}`}>
                                            {inv.Status || "—"}
                                        </span>
                                        {inv.ReceiptUrl && (
                                            <button
                                                onClick={() => window.open(inv.ReceiptUrl.startsWith('http') ? inv.ReceiptUrl : `http://localhost:8080/${inv.ReceiptUrl.replace(/\\/g, '/')}`, '_blank')}
                                                className="w-6 h-6 flex items-center justify-center bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors tooltip"
                                                title="View Uploaded Receipt"
                                            >
                                                <FontAwesomeIcon icon={faImage} />
                                            </button>
                                        )}
                                        {inv.items && inv.items.length > 0 && (
                                            <button
                                                onClick={() => generateInvoicePDF(inv, inv.items, {})}
                                                className="w-6 h-6 flex items-center justify-center bg-gray-100 dark:bg-cardbg text-gray-600 dark:text-textsecondary rounded hover:bg-gray-200 dark:hover:bg-actionitembg transition-colors tooltip"
                                                title="Download Invoice PDF"
                                            >
                                                <FontAwesomeIcon icon={faDownload} />
                                            </button>
                                        )}
                                        {inv.Status === 'PENDING' && (
                                            <button
                                                onClick={() => handleMarkPaid(inv.InvoiceId)}
                                                className="px-2 py-1 bg-[#166534] text-white text-[10px] font-bold tracking-wider rounded uppercase hover:bg-green-700 transition-colors shadow-sm"
                                            >
                                                Mark Paid
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const paginatedData = invoices.slice((pagination.pageNumber - 1) * pagination.pageSize, pagination.pageNumber * pagination.pageSize).map((item, idx) => ({ ...item, pageIndex: idx }));

    return (
        <div className="flex-1 flex flex-col shrink-0">
            <CommonTable
                loading={loading}
                tableHeading="All Maintenance Records"
                headers={tableHeaders}
                data={paginatedData}
                renderCell={(key, row) => renderTableCell(key, row)}
                expandedRowRender={expandedRowRender}
                pagination={{ ...pagination, totalRecords: invoices.length }}
                onPageChange={(page) => setPagination(prev => ({ ...prev, pageNumber: page }))}
                onLimitChange={(limit) => setPagination(prev => ({ ...prev, pageSize: limit, pageNumber: 1 }))}
                hideScrollbar={false}
                isLoading={loading}
            />
        </div>
    );
};

export default MaintenanceSetup;
